import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Modal, Typography as AntTypography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Typography } from '../design-system/typography';
import { colors, spacing } from '../design-system/tokens';
import { SharedContainerInfo } from './types';

const { Text } = AntTypography;

interface SharedContainerIndicatorProps {
  isShared: boolean;
  myNetWeight?: number;
  totalNetWeight?: number;
  sharedWith?: SharedContainerInfo[];
  validationError?: string | null;
  containerNumber: string;
}

export const SharedContainerIndicator: React.FC<SharedContainerIndicatorProps> = ({
  isShared,
  myNetWeight,
  totalNetWeight,
  sharedWith = [],
  validationError,
  containerNumber,
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  if (!isShared) {
    return null;
  }

  const formatWeight = (weight?: number) => {
    if (weight === undefined || weight === null) return 'N/A';
    return `${weight.toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs`;
  };

  const handleShowDetails = () => {
    setModalVisible(true);
  };

  const alertMessage = (
    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
      <InfoCircleOutlined style={{ fontSize: '16px', color: colors.tertiary }} />
      <div>
        <Text strong>Shared Container</Text>
        <div style={{ marginTop: spacing.xs }}>
          <Text style={{ fontSize: '13px' }}>
            Your Net Weight: {formatWeight(myNetWeight)} | Total Container: {formatWeight(totalNetWeight)}
          </Text>
        </div>
        {sharedWith.length > 0 && (
          <div style={{ marginTop: spacing.xs }}>
            <Text
              style={{ fontSize: '12px', color: colors.tertiary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={handleShowDetails}
            >
              View {sharedWith.length} other shipment{sharedWith.length > 1 ? 's' : ''} in this container
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Alert
        type={validationError ? 'error' : 'info'}
        message={validationError ? `Data Error: ${validationError}` : alertMessage}
        showIcon={false}
        style={{
          marginBottom: spacing.md,
          backgroundColor: validationError ? colors.status.negative : '#E6F7FF',
          border: `1px solid ${validationError ? colors.status.negative : colors.tertiary}`,
        }}
      />

      <Modal
        title={`Shared Container ${containerNumber}`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ marginBottom: spacing.lg }}>
          <Typography variant="label" as="div" style={{ marginBottom: spacing.xs, color: colors.text.primary }}>
            Weight Summary
          </Typography>
          <div style={{ display: 'flex', gap: spacing.xl }}>
            <div>
              <Text type="secondary">Your Net Weight:</Text>
              <div><Text strong>{formatWeight(myNetWeight)}</Text></div>
            </div>
            <div>
              <Text type="secondary">Total Container Weight:</Text>
              <div><Text strong>{formatWeight(totalNetWeight)}</Text></div>
            </div>
          </div>
        </div>

        <div>
          <Typography variant="label" as="div" style={{ marginBottom: spacing.xs, color: colors.text.primary }}>
            Other Shipments in This Container
          </Typography>
          {sharedWith.map((info, index) => (
            <div
              key={index}
              style={{
                padding: spacing.md,
                marginBottom: spacing.sm,
                backgroundColor: colors.background.secondary,
                borderRadius: '4px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                  <Link
                    to={`/contracts/${info.contractRef}`}
                    style={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: colors.tertiary,
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    {info.shipmentRef}
                  </Link>
                  <div style={{ fontSize: '13px', color: colors.text.secondary }}>
                    {info.buyer}
                  </div>
                  <div style={{ fontSize: '12px', color: colors.text.tertiary }}>
                    Contract: {info.contractRef}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13px', color: colors.text.secondary }}>
                    Net Weight
                  </div>
                  <Text strong>{formatWeight(info.netWeight)}</Text>
                  <div style={{ fontSize: '12px', color: colors.text.tertiary }}>
                    {info.goodsCount} goods item{info.goodsCount !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {validationError && (
          <Alert
            type="error"
            message={validationError}
            description="This container has inconsistent data. Please contact support."
            showIcon
            style={{ marginTop: spacing.md }}
          />
        )}
      </Modal>
    </>
  );
};

export default SharedContainerIndicator;
